import { computed, onMounted, ref } from 'vue'
import type * as Markerjs3 from '@markerjs/markerjs3'
import { useMarkerjs3 } from '@collection-player/composables/markerjs.use.ts'
import { useI18n } from 'vue-i18n'
import useToast from '@lib/composables/useToast.ts'
import type { MarkerArea, MarkerBase } from '@markerjs/markerjs3'

const laserPointerMarker = ref<typeof Markerjs3.MarkerBase | null>(null)
const markerjs3 = ref<typeof Markerjs3 | null>(null)

export default function useAnnotation() {
  const storage = new Map<string, any>()
  const { t } = useI18n()
  const isReady = ref(false)
  const { toast } = useToast()
  const activeMarker = ref<null | MarkerArea>(null)
  const activeTool = ref<null | typeof MarkerBase | string>(null)
  const isVisibleMenu = ref(false)
  const isDrawingActive = computed(() => !!activeTool.value)

  // Switch between viewing (slide interaction) and editing (tools interaction)
  const enablePointerEventsInSvg = (value = true) => {
    // @ts-expect-error check if _mainCanvas is defined
    if (!activeMarker.value?._mainCanvas?.style) return

    // @ts-expect-error toggle interaction with the canvas (viewing vs editing)
    activeMarker.value._mainCanvas.style.pointerEvents = value ? 'auto' : 'none'
  }

  const setActiveMarker = (marker: null | MarkerArea) => {
    if (activeMarker.value) {
      clearSelection()
    }

    activeMarker.value = marker
    triggerToolUpdate()
  }

  const setActiveTool = (tool: null | typeof MarkerBase | string) => {
    activeTool.value = tool
    triggerToolUpdate()
  }

  const triggerToolUpdate = () => {
    if (!activeMarker.value) {
      return
    }

    if (!activeTool.value) {
      enablePointerEventsInSvg(false)

      return
    }

    enablePointerEventsInSvg(true)

    if (activeTool.value === 'selection') {
      activeMarker.value.switchToSelectMode()
    } else if (typeof activeTool.value === 'function') {
      activeMarker.value.createMarker(activeTool.value)
    }
  }

  const clearSelection = () => {
    if (!activeMarker.value) {
      return
    }

    activeMarker.value.deselectEditor()
    activeMarker.value.deleteSelectedMarkers()
  }

  const activeToolIcon = computed(() => {
    if (!activeTool.value) {
      return markerTools.value.handPointer.icon
    }

    const toolConfig = Object.values(markerTools.value).find((tool) => tool.marker === activeTool.value)

    return toolConfig ? toolConfig.icon : markerTools.value.handPointer.icon
  })

  const clearStorage = () => {
    storage.clear()
  }

  const handleAnnotationSelect = (action: string) => {
    isVisibleMenu.value = false

    const markerInstance = activeMarker.value

    if (!markerInstance) {
      console.warn('No active marker instance found')
      return
    }

    const toolConfig = markerTools.value[action]

    if (!toolConfig) {
      console.warn(`Unknown marker type: ${action}`)
      return
    }

    if (toolConfig.action) {
      toolConfig.action()
      clearSelection()

      return
    }

    setActiveTool(toolConfig.marker)
    clearSelection()
  }

  const markerTools = computed<{
    [key: string]: {
      marker: typeof Markerjs3.MarkerBase | 'selection' | 'eraseSelected' | 'eraseAll' | null
      icon: string
      translationKey: string
      action?: () => void
      dividerBefore?: boolean
    }
  }>(() => ({
    handPointer: {
      marker: null,
      icon: 'hand-pointer',
      translationKey: t('canvasUI.collectionPlayer.annotations.swipe'),
    },
    ...(markerjs3.value
      ? {
          arrow: {
            dividerBefore: true,
            marker: 'selection',
            icon: 'arrow-pointer',
            translationKey: t('canvasUI.collectionPlayer.annotations.selection'),
          },
          pointer: {
            marker: laserPointerMarker.value,
            icon: 'circle-dot',
            translationKey: t('canvasUI.collectionPlayer.annotations.laserPointer'),
          },
          highlighter: {
            marker: markerjs3.value.HighlighterMarker,
            icon: 'highlighter',
            translationKey: t('canvasUI.collectionPlayer.annotations.highlighter'),
          },
          pen: {
            marker: markerjs3.value.FreehandMarker,
            icon: 'pencil-mechanical',
            translationKey: t('canvasUI.collectionPlayer.annotations.pen'),
          },
          eraseSelected: {
            marker: 'eraseSelected',
            icon: 'eraser',
            translationKey: t('canvasUI.collectionPlayer.annotations.eraseSelected'),
            dividerBefore: true,
            action: () => {
              const markerInstance = activeMarker.value

              if (!markerInstance) return

              markerInstance.deleteSelectedMarkers()
            },
          },
          eraseAll: {
            marker: 'eraseAll',
            icon: 'trash',
            translationKey: t('canvasUI.collectionPlayer.annotations.eraseAll'),
            action: () => {
              const markerInstance = activeMarker.value

              if (!markerInstance) return

              const editors = [...markerInstance.editors]

              editors.forEach((editor) => {
                markerInstance.deleteMarker(editor)
              })
            },
          },
        }
      : {}),
  }))

  const resetAnnotations = (notify?: boolean) => {
    if (!activeMarker.value) return

    const editors = [...activeMarker.value.editors]

    if (notify && editors.length > 0) {
      toast.warning(t('canvasUI.canvasBuilder.components.clearAnnotationsWarning'))
    }

    editors.forEach((editor) => {
      activeMarker.value?.deleteMarker(editor)
    })
  }

  const applyShadowRootStyles = (shadowRoot: ShadowRoot) => {
    const style = document.createElement('style')

    style.textContent = `
    :host {
      outline: none;
    }

    .canvas-container > svg {
      transform: none !important;
      position: relative;
      cursor: crosshair !important;
      margin: 0 !important;
    }

    .markerjs-container {
      width: 100% !important;
      height: 100% !important;
    }

    .markerjs-container canvas {
      width: 100% !important;
      height: 100% !important;
    }
  `

    shadowRoot.appendChild(style)
  }

  const createNewImage = (width: number, height: number) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    return canvas.toDataURL()
  }

  onMounted(async () => {
    const {
      laserPointer: { LaserPointerMarker },
      markerjs3: Markerjs3,
    } = await useMarkerjs3()

    laserPointerMarker.value = LaserPointerMarker
    markerjs3.value = Markerjs3
    isReady.value = true
  })

  return {
    isVisibleMenu,
    markerTools,
    activeToolIcon,
    activeTool,
    isReady,
    handleAnnotationSelect,
    setActiveMarker,
    activeMarker,
    showMenu: (value: boolean) => (isVisibleMenu.value = value),
    toggleMenu: () => (isVisibleMenu.value = !isVisibleMenu.value),
    storage,
    resetAnnotations,
    triggerToolUpdate,
    clearStorage,
    applyShadowRootStyles,
    createNewImage,
    isDrawingActive,
  }
}
